import { CuriosityValue } from '@procureai/curiosity-ui-mui';
import { DateFormatPreset, NumberFormatPreset, formatDate, formatNumber } from '@procureai/ui-mui';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { I18nNamespace } from '~/common/types';

import { BaseBusinessLogItem, BusinessLogItemProps } from './BaseBusinessLogItem';
import { MetaInformation } from './MetaInformation';
import { SAVING_FIELD_CONFIG, SavingFieldSpec, humanizeFieldKey } from './savingFieldConfig';

const FIELD_LABEL_PREFIX = 'Savings.form.fields';

const ChangeValue = ({ value, spec }: { value: unknown; spec?: SavingFieldSpec }) => {
  const { t } = useTranslation(I18nNamespace.common);

  if (value === null || value === undefined) {
    return <>—</>;
  }
  const items = Array.isArray(value) ? value : [value];
  if (items.length === 0) {
    return <>—</>;
  }

  const formatScalar = (raw: unknown) => {
    if (spec?.format === 'date') {
      return formatDate(DateFormatPreset.TWO_DIGIT, new Date(raw as string));
    }
    if (spec?.format === 'decimal') {
      return formatNumber(NumberFormatPreset.DECIMAL, Number(raw));
    }
    if (spec?.enumPrefix) {
      return t(`${spec.enumPrefix}.${String(raw)}`);
    }
    return String(raw);
  };

  return (
    <>
      {items.map((item, index) => (
        <Fragment key={String(item)}>
          {index > 0 ? ', ' : null}
          {spec?.ns ? (
            <CuriosityValue ns={spec.ns} i18nKey={String(item)} fallback={String(item)} />
          ) : (
            formatScalar(item)
          )}
        </Fragment>
      ))}
    </>
  );
};

const SavingFieldChangeMessage = ({ context }: { context: Record<string, unknown> }) => {
  const { t } = useTranslation(I18nNamespace.common);
  const field = String(context.field ?? '');
  const spec = SAVING_FIELD_CONFIG[field];
  const label = spec?.labelKey ? t(`${FIELD_LABEL_PREFIX}.${spec.labelKey}.label`) : humanizeFieldKey(field);

  return (
    <>
      {label}: <ChangeValue value={context.before} spec={spec} /> → <ChangeValue value={context.after} spec={spec} />
    </>
  );
};

export const SavingFieldLogItem = ({ item, virtual }: BusinessLogItemProps) => {
  const { actorId, actorVariant, createdAt, context } = item;

  return (
    <BaseBusinessLogItem
      item={item}
      virtual={virtual}
      primary={<SavingFieldChangeMessage context={context as Record<string, unknown>} />}
      secondary={<MetaInformation actorId={actorId} actorVariant={actorVariant} createdAt={createdAt} />}
    />
  );
};
